import { ok } from 'neverthrow';
import type {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { WorkflowEditorToolbarActionBase } from './workflow-editor-toolbar-action.base';
import {
  workflowCanvasHasNodeSelection,
  workflowModelFromContext,
} from './workflow-toolbar-action.helpers';
import { listNestedWorkflowReferences } from './nested-workflow-tree';
import { WORKFLOW_TREE_VIEW_TITLE } from './workflow-artifact-views';

export const OPEN_HIERARCHY_ACTIVE_TITLE =
  'Show this workflow and every workflow it runs, as one graph';

export type WorkflowHierarchyViewOps = {
  /** False when there is no artifact stack to open a view into. */
  canOpen: () => boolean;
  open: () => void;
};

/**
 * Opens the hierarchy view: this workflow and everything it runs, as one graph.
 *
 * Applicable only where there is something to show — a workflow that runs no
 * other workflow has no hierarchy, and offering the view for the flat graphs that
 * are the common case would promise one. Reads that off the model's live draft
 * rather than the saved graph, so a `run-workflow` node added a moment ago counts.
 *
 * Stands down while a node is selected. The toolbar is then showing what can be
 * done to *that node* — duplicate it, delete it — and this is about the workflow
 * as a whole, so it would read as one of them. Gated on the same predicate
 * `WorkflowToolbarDuplicateSelectionAction` uses, so the two are exact
 * complements: this shows precisely when the node actions do not.
 */
export class WorkflowToolbarOpenHierarchyAction extends WorkflowEditorToolbarActionBase {
  title = WORKFLOW_TREE_VIEW_TITLE;
  override description = OPEN_HIERARCHY_ACTIVE_TITLE;
  group = 'Workflow';
  icon = 'list-tree';

  constructor(private readonly ops: WorkflowHierarchyViewOps) {
    super();
  }

  override applicable(context: ApplicableContext): boolean {
    if (!this.ops.canOpen() || workflowCanvasHasNodeSelection(context)) {
      return false;
    }
    const graph =
      workflowModelFromContext(context)?.graphWatchable.snapshot() ?? null;
    return graph !== null && listNestedWorkflowReferences(graph).length > 0;
  }

  override async execute(
    _params: Record<string, never>,
    _context: ExecuteContext
  ): Promise<ActionResult<{ t: string }[]>> {
    this.ops.open();
    return ok({});
  }
}
